import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Trash2, Plus, ArrowLeft, Search, Upload, Sparkles, CheckCircle2, Lock, Unlock } from "lucide-react";
import { AcornPlaceholder, absMediaUrl } from "@/components/AcornPlaceholder";
import AIProposedPanel from "./AIProposedPanel";
import DuplicateSuspectDialog from "./DuplicateSuspectDialog";

const TYPES = ["rosso", "bianco", "rosato", "bollicine", "dolce"];
const STATUSES = ["pending_review", "da_arricchire", "arricchito_in_validazione", "validato_sommelier"];
const VISIBILITIES = ["auto", "force_visible", "force_hidden"];
const SIZES = ["0.375", "0.75", "1.5"];

// Fase 8.9j · campi lockabili contro sovrascritture AI/Mexal.
// Deve corrispondere a `LOCKABLE_FIELDS` nel backend (server.py).
const LOCKABLE_FIELDS = ["producer", "categoria", "type", "denomination"];

const EMPTY = {
    name: "", producer: "", producer_website: null, vintage: "", type: "rosso",
    country: "Italia", region: "", provincia: null, denomination: "",
    organoleptic: { grapes: [], tasting_notes: "", pairing: "", description: "" },
    glass_available: false,
    formats: [{ size: "0.75", base_price_public: 0, price_override: null, stock_quantity: 0, mexal_sku: null }],
    status: "da_arricchire", source: "manual", admin_visible_override: "auto",
    previous_vintage_wine_id: null,
    featured: false,
    images: { thumb_url: null, card_url: null, status: "none" },
    raw_mexal_description: null,
    ai_proposed: null,
    // Fase 8.5 · categoria e gradazione (utili soprattutto per distillati/liquori/birre)
    categoria: "vino",
    alcohol_content: null,
    // Fase 8.9j · campi lockati contro overwrite AI/Mexal.
    locked_fields: [],
};


/**
 * FIX 8.9j · piccolo pulsante lucchetto cliccabile accanto ai campi
 * lockabili (producer/categoria/type/denomination). Se il vino non è
 * ancora persistito (isNew) mostra l'icona ma disabilita il click con
 * tooltip esplicativo.
 *
 * Contract:
 *   - locked (bool): stato corrente lettura dal parent.
 *   - onToggle(nextLocked): callback ottimistico che aggiorna anche il
 *     parent state; l'helper esegue la chiamata PUT sul backend e in
 *     caso di errore chiede al parent di rollbackare.
 */
function LockButton({ wineId, field, locked, allLocked, onCommit, isNew }) {
    const disabled = isNew || !wineId;
    const label = locked ? `${field} bloccato — clicca per sbloccare`
                          : `Blocca ${field} contro overwrite AI/Mexal`;
    const onClick = async (e) => {
        e.preventDefault();
        if (disabled) return;
        const next = new Set(allLocked);
        if (locked) next.delete(field); else next.add(field);
        const nextArr = LOCKABLE_FIELDS.filter((f) => next.has(f));
        try {
            const { data } = await api.put(`/admin/wines/${wineId}/locked-fields`,
                { locked_fields: nextArr });
            onCommit(data.locked_fields || nextArr);
            toast.success(locked ? `Sblocco: ${field}` : `Blocco: ${field}`);
        } catch (err) {
            toast.error(`Impossibile aggiornare il lock: ${formatApiErrorDetail(err) || "errore"}`);
        }
    };
    const Icon = locked ? Lock : Unlock;
    return (
        <button
            type="button"
            onClick={onClick}
            disabled={disabled}
            title={disabled ? "Salva prima il vino per gestire il lock" : label}
            data-testid={`wine-editor-lock-${field}`}
            aria-pressed={locked}
            className={`inline-flex items-center justify-center w-6 h-6 rounded-full transition-colors ${
                locked
                    ? "text-[hsl(var(--primary))] bg-[hsl(var(--primary))]/10 hover:bg-[hsl(var(--primary))]/20"
                    : "text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--primary))] hover:bg-[hsl(var(--secondary))]"
            } ${disabled ? "opacity-40 cursor-not-allowed" : ""}`}
        >
            <Icon className="w-3.5 h-3.5" strokeWidth={1.5} />
        </button>
    );
}

export default function AdminWineEditor() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const isNew = !id;
    // Fase 8.5 · categoria da query string (per "Nuovo prodotto" da /admin/distillati)
    const catFromUrl = searchParams.get("categoria");
    const [form, setForm] = useState(() => ({
        ...EMPTY,
        ...(catFromUrl && ["distillato", "liquore", "birra", "altro"].includes(catFromUrl)
            ? { categoria: catFromUrl }
            : {}),
    }));
    const [loading, setLoading] = useState(!isNew);
    const [saving, setSaving] = useState(false);
    // Fase 8.3 · dialog duplicati + publish state
    const [dupDialog, setDupDialog] = useState({ open: false, candidates: [] });
    const [publishing, setPublishing] = useState(false);

    // Fase 8.5 · derivato: siamo su un non-vino? Se sì l'editor mostra
    // gradazione + rende annata/vitigni opzionali (non-obbligatori).
    const isNonWine = ["distillato", "liquore", "birra", "altro"].includes(form.categoria);

    const reload = () => {
        if (isNew) return;
        setLoading(true);
        api.get(`/admin/wines/${id}`)
            .then(({ data }) => {
                setForm({
                    ...EMPTY, ...data,
                    vintage: data.vintage ?? "",
                    organoleptic: {
                        grapes: data.organoleptic?.grapes || [],
                        tasting_notes: data.organoleptic?.tasting_notes || "",
                        pairing: data.organoleptic?.pairing || "",
                        description: data.organoleptic?.description || "",
                    },
                    images: data.images || { thumb_url: null, card_url: null, status: "none" },
                    formats: (data.formats || []).map((f) => ({
                        size: f.size,
                        base_price_public: f.base_price_public ?? 0,
                        price_override: f.price_override ?? null,
                        stock_quantity: f.stock_quantity ?? 0,
                        mexal_sku: f.mexal_sku ?? null,
                    })),
                    ai_proposed: data.ai_proposed ?? null,
                    locked_fields: Array.isArray(data.locked_fields) ? data.locked_fields : [],
                });
            })
            .catch(() => toast.error("Vino non trovato"))
            .finally(() => setLoading(false));
    };
    useEffect(() => {
        reload();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, isNew]);

    const [imgBusy, setImgBusy] = useState(false);
    const [enriching, setEnriching] = useState(false);

    const doImageSearch = async () => {
        setImgBusy(true);
        try {
            const { data } = await api.post(`/admin/wines/${id}/image/search`);
            setForm((f) => ({ ...f, images: data.images }));
            toast.success("Immagine trovata");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Ricerca fallita");
        } finally { setImgBusy(false); }
    };

    const doImageUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setImgBusy(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post(`/admin/wines/${id}/image/upload`, fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setForm((f) => ({ ...f, images: data.images }));
            toast.success("Immagine caricata");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Upload fallito");
        } finally {
            setImgBusy(false);
            e.target.value = "";
        }
    };

    const doEnrich = async () => {
        setEnriching(true);
        try {
            const { data } = await api.post(`/admin/ai/enrich-wine/${id}`);
            const enr = data.enriched || {};
            setForm((f) => ({
                ...f,
                organoleptic: {
                    ...f.organoleptic,
                    grapes: enr.grapes && enr.grapes.length ? enr.grapes : f.organoleptic.grapes,
                    tasting_notes: enr.tasting_notes || f.organoleptic.tasting_notes,
                    pairing: enr.pairing || f.organoleptic.pairing,
                    description: enr.description || f.organoleptic.description,
                },
                region: enr.region && !f.region ? enr.region : f.region,
                denomination: enr.denomination && !f.denomination ? enr.denomination : f.denomination,
                status: "arricchito_in_validazione",
            }));
            toast.success("Scheda arricchita dall'AI");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Errore AI");
        } finally { setEnriching(false); }
    };

    const set = (patch) => setForm((f) => ({ ...f, ...patch }));
    const setOrg = (patch) => setForm((f) => ({ ...f, organoleptic: { ...f.organoleptic, ...patch } }));

    const updateFormat = (idx, patch) => {
        setForm((f) => ({
            ...f,
            formats: f.formats.map((fmt, i) => (i === idx ? { ...fmt, ...patch } : fmt)),
        }));
    };
    const addFormat = () => {
        setForm((f) => ({
            ...f,
            formats: [...f.formats, { size: "0.75", base_price_public: 0, price_override: null, stock_quantity: 0, mexal_sku: null }],
        }));
    };
    const removeFormat = (idx) => {
        setForm((f) => ({ ...f, formats: f.formats.filter((_, i) => i !== idx) }));
    };

    const buildPayload = () => ({
        ...form,
        vintage: form.vintage === "" ? null : Number(form.vintage),
        formats: form.formats.map((f) => ({
            size: f.size,
            base_price_public: Number(f.base_price_public) || 0,
            price_override: f.price_override === "" || f.price_override === null ? null : Number(f.price_override),
            stock_quantity: f.stock_quantity === "" || f.stock_quantity === null ? null : Number(f.stock_quantity),
            mexal_sku: f.mexal_sku || null,
        })),
    });

    const doCreate = async (ackDuplicates) => {
        const payload = buildPayload();
        const params = ackDuplicates ? "?ack_duplicates=true" : "";
        return api.post(`/admin/wines${params}`, payload);
    };

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            if (isNew) {
                try {
                    const { data } = await doCreate(false);
                    toast.success(
                        data?.pipeline_enqueued
                            ? "Vino creato · arricchimento AI in corso (verrà in Segnalazioni)"
                            : "Vino creato"
                    );
                    navigate("/admin/vini");
                } catch (err) {
                    const detail = err.response?.data?.detail;
                    if (err.response?.status === 409
                        && detail && typeof detail === "object"
                        && detail.reason === "duplicate_candidates") {
                        setDupDialog({ open: true, candidates: detail.candidates || [] });
                        setSaving(false);
                        return;
                    }
                    throw err;
                }
            } else {
                await api.put(`/admin/wines/${id}`, buildPayload());
                toast.success("Modifiche salvate");
                navigate("/admin/vini");
            }
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore nel salvataggio");
        } finally {
            setSaving(false);
        }
    };

    const proceedFromDialog = async () => {
        setDupDialog({ open: false, candidates: [] });
        setSaving(true);
        try {
            const { data } = await doCreate(true);
            toast.success(
                data?.pipeline_enqueued
                    ? "Vino creato · verrà in Segnalazioni (duplicati registrati)"
                    : "Vino creato"
            );
            navigate("/admin/vini");
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore nel salvataggio");
        } finally { setSaving(false); }
    };

    const publish = async () => {
        setPublishing(true);
        try {
            await api.post(`/admin/wines/${id}/publish`);
            toast.success("Vino pubblicato · ora visibile nel catalogo");
            reload();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore pubblicazione");
        } finally { setPublishing(false); }
    };

    if (loading) return <div className="text-[hsl(var(--muted-foreground))]">Caricamento…</div>;

    return (
        <div className="max-w-3xl" data-testid="admin-wine-editor">
            <button
                type="button"
                onClick={() => navigate("/admin/vini")}
                className="flex items-center gap-2 text-sm text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--primary))] mb-4"
                data-testid="admin-wine-editor-back"
            >
                <ArrowLeft className="w-4 h-4" /> Torna all'elenco
            </button>

            <h1 className="font-serif text-3xl mb-6">{isNew ? "Nuovo Vino" : "Modifica Vino"}</h1>

            {/* Fase 8.3 · banner "pending_review" + pulsante Publish */}
            {!isNew && form.status === "pending_review" && (
                <div className="bg-[#FDF6E4] border border-[#D9B98F] rounded-sm p-4 mb-4 flex items-start gap-3" data-testid="pending-review-banner">
                    <Sparkles className="w-5 h-5 text-[#B87500] mt-0.5 shrink-0" strokeWidth={1.5} />
                    <div className="flex-1 text-sm">
                        <div className="font-medium mb-1">In attesa di revisione</div>
                        <p className="text-[hsl(var(--muted-foreground))] text-xs">
                            Questo vino non è ancora visibile nel catalogo pubblico.
                            Rivedi i dati AI proposti (sotto) e conferma i duplicati sospetti in
                            <b> Segnalazioni</b>, poi pubblica per renderlo visibile.
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={publish}
                        disabled={publishing}
                        className="co-btn-primary text-xs shrink-0"
                        data-testid="wine-editor-publish"
                    >
                        <CheckCircle2 className="w-4 h-4" strokeWidth={1.5} />
                        {publishing ? "Pubblicazione…" : "Pubblica ora"}
                    </button>
                </div>
            )}

            {/* Fase 8.3 · pannello dati AI proposti (visibile solo su vini esistenti manual/photo/file) */}
            {!isNew && ["manual", "photo", "file"].includes(form.source) && (
                <div className="mb-6">
                    <AIProposedPanel
                        wineId={id}
                        aiProposed={form.ai_proposed}
                        currentValues={form}
                        onChanged={reload}
                    />
                </div>
            )}

            <DuplicateSuspectDialog
                open={dupDialog.open}
                onOpenChange={(v) => setDupDialog((s) => ({ ...s, open: v }))}
                candidates={dupDialog.candidates}
                onProceed={proceedFromDialog}
                onCancel={() => setDupDialog({ open: false, candidates: [] })}
            />

            <form onSubmit={submit} className="space-y-8">
                {/* Anagrafica */}
                <Section title="Anagrafica">
                    <Field label="Nome" required>
                        <Input value={form.name} onChange={(e) => set({ name: e.target.value })} required data-testid="wine-editor-name" />
                    </Field>
                    <Field
                        label="Produttore"
                        required
                        action={<LockButton
                            wineId={id} field="producer"
                            locked={form.locked_fields.includes("producer")}
                            allLocked={form.locked_fields}
                            onCommit={(next) => set({ locked_fields: next })}
                            isNew={isNew}
                        />}
                    >
                        <Input value={form.producer} onChange={(e) => set({ producer: e.target.value })} required data-testid="wine-editor-producer" />
                    </Field>
                    <Field label="Sito web produttore (opzionale)">
                        <Input
                            type="url"
                            placeholder="https://…"
                            value={form.producer_website || ""}
                            onChange={(e) => set({ producer_website: e.target.value || null })}
                            data-testid="wine-editor-producer-website"
                        />
                    </Field>
                    {/* Fase 8.5 · Categoria: selettore + gradazione appare per non-vino */}
                    <div className="grid grid-cols-2 gap-4">
                        <Field
                            label="Categoria"
                            required
                            action={<LockButton
                                wineId={id} field="categoria"
                                locked={form.locked_fields.includes("categoria")}
                                allLocked={form.locked_fields}
                                onCommit={(next) => set({ locked_fields: next })}
                                isNew={isNew}
                            />}
                        >
                            <Select value={form.categoria || "vino"} onValueChange={(v) => set({ categoria: v })}>
                                <SelectTrigger data-testid="wine-editor-categoria"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="vino">Vino</SelectItem>
                                    <SelectItem value="distillato">Distillato</SelectItem>
                                    <SelectItem value="liquore">Liquore</SelectItem>
                                    <SelectItem value="birra">Birra</SelectItem>
                                    <SelectItem value="altro">Altro</SelectItem>
                                </SelectContent>
                            </Select>
                        </Field>
                        <Field label={`Gradazione alcolica (% vol)${isNonWine ? "" : " — opzionale"}`}>
                            <Input
                                type="number" step="0.1" min="0" max="100"
                                value={form.alcohol_content ?? ""}
                                onChange={(e) => set({ alcohol_content: e.target.value === "" ? null : Number(e.target.value) })}
                                placeholder={isNonWine ? "es. 42.0" : "es. 13.5"}
                                data-testid="wine-editor-alcohol"
                            />
                        </Field>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <Field label={isNonWine ? "Annata (opzionale)" : "Annata"}>
                            <Input type="number" min="1900" max="2100" value={form.vintage} onChange={(e) => set({ vintage: e.target.value })} data-testid="wine-editor-vintage" />
                        </Field>
                        {/* Fase 8.5 · Tipologia obbligatoria solo per i vini */}
                        {!isNonWine && (
                            <Field
                                label="Tipologia"
                                required
                                action={<LockButton
                                    wineId={id} field="type"
                                    locked={form.locked_fields.includes("type")}
                                    allLocked={form.locked_fields}
                                    onCommit={(next) => set({ locked_fields: next })}
                                    isNew={isNew}
                                />}
                            >
                                <Select value={form.type} onValueChange={(v) => set({ type: v })}>
                                    <SelectTrigger data-testid="wine-editor-type"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </Field>
                        )}
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <Field label="Paese" required>
                            <Input value={form.country} onChange={(e) => set({ country: e.target.value })} required data-testid="wine-editor-country" />
                        </Field>
                        <Field label={isNonWine ? "Regione / Zona (opzionale)" : "Regione / Zona"} required={!isNonWine}>
                            <Input value={form.region} onChange={(e) => set({ region: e.target.value })} required={!isNonWine} data-testid="wine-editor-region" />
                        </Field>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-3">
                        <Field label="Provincia (solo Italia)">
                            <Input value={form.provincia || ""} onChange={(e) => set({ provincia: e.target.value || null })}
                                placeholder="es. Cuneo, Siena, Catania"
                                data-testid="wine-editor-provincia" />
                        </Field>
                        <Field
                            label="Denominazione"
                            action={<LockButton
                                wineId={id} field="denomination"
                                locked={form.locked_fields.includes("denomination")}
                                allLocked={form.locked_fields}
                                onCommit={(next) => set({ locked_fields: next })}
                                isNew={isNew}
                            />}
                        >
                            <Input value={form.denomination} onChange={(e) => set({ denomination: e.target.value })} data-testid="wine-editor-denomination" />
                        </Field>
                    </div>
                </Section>

                {/* Organoleptic (Fase 8.5: label + campi variano se non-vino) */}
                <Section title={isNonWine ? "Note & servizio" : "Note organolettiche"} action={!isNew ? (
                    <button type="button" onClick={doEnrich} disabled={enriching} className="co-btn-ghost text-xs" data-testid="wine-editor-ai-enrich">
                        <Sparkles className="w-3.5 h-3.5" /> {enriching ? "Arricchimento…" : "Arricchisci con AI"}
                    </button>
                ) : null}>
                    {!isNonWine && (
                        <Field label="Uvaggio (vitigni, separati da virgola)">
                            <Input
                                value={(form.organoleptic.grapes || []).join(", ")}
                                onChange={(e) => setOrg({ grapes: e.target.value.split(",").map((g) => g.trim()).filter(Boolean) })}
                                data-testid="wine-editor-grapes"
                                placeholder="Es. Nebbiolo · Corvina, Rondinella, Molinara"
                            />
                        </Field>
                    )}
                    <Field label={isNonWine ? "Note di degustazione (aroma, gusto, finale)" : "Note di degustazione"}>
                        <Textarea rows={3} value={form.organoleptic.tasting_notes} onChange={(e) => setOrg({ tasting_notes: e.target.value })} data-testid="wine-editor-tasting" />
                    </Field>
                    <Field label={isNonWine ? "Note di servizio / abbinamenti" : "Abbinamenti"}>
                        <Textarea rows={2} value={form.organoleptic.pairing} onChange={(e) => setOrg({ pairing: e.target.value })} data-testid="wine-editor-pairing" />
                    </Field>
                    {form.raw_mexal_description && (
                        <div className="mt-2 p-3 bg-[hsl(var(--secondary))] rounded-sm">
                            <div className="co-caption mb-1">Descrizione grezza Mexal</div>
                            <div className="text-sm font-mono text-[hsl(var(--muted-foreground))]">{form.raw_mexal_description}</div>
                        </div>
                    )}
                </Section>

                {/* Immagine */}
                {!isNew && (
                <Section title="Immagine" action={
                    <div className="flex gap-2">
                        <button
                            type="button" onClick={doImageSearch} disabled={imgBusy}
                            className="co-btn-ghost text-xs" data-testid="wine-editor-image-search"
                            title="Cerca automaticamente"
                        >
                            <Search className="w-3.5 h-3.5" /> {imgBusy ? "…" : "Cerca di nuovo"}
                        </button>
                        <label className="co-btn-ghost text-xs cursor-pointer" data-testid="wine-editor-image-upload-label">
                            <Upload className="w-3.5 h-3.5" /> Carica
                            <input
                                type="file"
                                accept="image/*"
                                onChange={doImageUpload}
                                className="hidden"
                                data-testid="wine-editor-image-upload"
                            />
                        </label>
                    </div>
                }>
                    <div className="flex items-center gap-6">
                        <div className="w-40 h-40 flex items-center justify-center bg-[hsl(var(--secondary))] rounded-sm">
                            {form.images?.card_url ? (
                                <img src={absMediaUrl(form.images.card_url)} alt="" className="max-h-full max-w-full object-contain" />
                            ) : (
                                <AcornPlaceholder size={100} opacity={0.55} />
                            )}
                        </div>
                        <div className="flex-1 text-sm text-[hsl(var(--muted-foreground))]">
                            <div>Stato: <span className="text-[hsl(var(--foreground))]">{form.images?.status || "none"}</span></div>
                            <div className="mt-2 text-xs">
                                Il caricamento manuale converte in WebP (thumb 150px, card 500px, max 80KB) e sovrascrive l'eventuale immagine automatica.
                            </div>
                        </div>
                    </div>
                </Section>
                )}

                {/* Disponibilità */}
                <Section title="Disponibilità e visibilità">
                    <label className="flex items-center gap-3 cursor-pointer text-sm">
                        <Switch checked={form.glass_available} onCheckedChange={(v) => set({ glass_available: v })} data-testid="wine-editor-glass" />
                        <span>Disponibile al calice</span>
                    </label>
                    <label className="flex items-center gap-3 cursor-pointer text-sm">
                        <Switch checked={form.featured} onCheckedChange={(v) => set({ featured: v })} data-testid="wine-editor-featured" />
                        <span>In evidenza · Selezione del Sommelier</span>
                    </label>
                    <div className="grid grid-cols-2 gap-4">
                        <Field label="Stato scheda">
                            <Select value={form.status} onValueChange={(v) => set({ status: v })}>
                                <SelectTrigger data-testid="wine-editor-status"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </Field>
                        <Field label="Visibilità catalogo">
                            <Select value={form.admin_visible_override} onValueChange={(v) => set({ admin_visible_override: v })}>
                                <SelectTrigger data-testid="wine-editor-visibility"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {VISIBILITIES.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </Field>
                    </div>
                </Section>

                {/* Formats */}
                <Section title="Formati" action={
                    <button type="button" className="co-btn-ghost text-xs" onClick={addFormat} data-testid="wine-editor-add-format">
                        <Plus className="w-3.5 h-3.5" /> Aggiungi formato
                    </button>
                }>
                    <div className="space-y-3">
                        {form.formats.map((fmt, idx) => (
                            <div key={idx} className="border border-[hsl(var(--border))] rounded-sm p-4 bg-white" data-testid={`wine-editor-format-${idx}`}>
                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                                    <Field label="Formato">
                                        <Select value={fmt.size} onValueChange={(v) => updateFormat(idx, { size: v })}>
                                            <SelectTrigger data-testid={`wine-editor-format-size-${idx}`}><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                                {SIZES.map((s) => <SelectItem key={s} value={s}>{s} L</SelectItem>)}
                                            </SelectContent>
                                        </Select>
                                    </Field>
                                    <Field label="Prezzo base €">
                                        <Input type="number" step="0.01" value={fmt.base_price_public} onChange={(e) => updateFormat(idx, { base_price_public: e.target.value })} data-testid={`wine-editor-format-price-${idx}`} />
                                    </Field>
                                    <Field label="Override €">
                                        <Input
                                            type="number" step="0.01"
                                            placeholder="—"
                                            value={fmt.price_override ?? ""}
                                            onChange={(e) => updateFormat(idx, { price_override: e.target.value })}
                                            data-testid={`wine-editor-format-override-${idx}`}
                                        />
                                    </Field>
                                    <Field label="Stock">
                                        <Input
                                            type="number" min="0"
                                            value={fmt.stock_quantity ?? ""}
                                            onChange={(e) => updateFormat(idx, { stock_quantity: e.target.value })}
                                            data-testid={`wine-editor-format-stock-${idx}`}
                                        />
                                    </Field>
                                </div>
                                <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    <Field label="Mexal SKU (opzionale — collega la giacenza al gestionale)">
                                        <Input
                                            placeholder="Es. 000601"
                                            value={fmt.mexal_sku ?? ""}
                                            onChange={(e) => updateFormat(idx, { mexal_sku: e.target.value || null })}
                                            data-testid={`wine-editor-format-sku-${idx}`}
                                        />
                                    </Field>
                                </div>
                                {form.formats.length > 1 && (
                                    <button
                                        type="button"
                                        onClick={() => removeFormat(idx)}
                                        className="mt-3 text-xs text-[hsl(var(--destructive))] hover:underline flex items-center gap-1"
                                        data-testid={`wine-editor-format-remove-${idx}`}
                                    >
                                        <Trash2 className="w-3 h-3" /> Rimuovi formato
                                    </button>
                                )}
                            </div>
                        ))}
                    </div>
                    {!isNew && <MexalSkuLookup onPick={(sku, desc) => {
                        // Add a new format wired to that SKU, size guessed from description
                        const guessSize = /150|1\.5|MAGNUM/i.test(desc) ? "1.5"
                            : /37\.?5|375|MEZZA|DEMI/i.test(desc) ? "0.375" : "0.75";
                        setForm((f) => ({
                            ...f,
                            formats: [...f.formats, { size: guessSize, base_price_public: 0, price_override: null, stock_quantity: 0, mexal_sku: sku }],
                        }));
                    }} />}
                </Section>

                <div className="flex gap-3">
                    <button type="button" className="co-btn-ghost" onClick={() => navigate("/admin/vini")}>Annulla</button>
                    <button type="submit" disabled={saving} className="co-btn-primary" data-testid="wine-editor-save">
                        {saving ? "Salvataggio…" : "Salva"}
                    </button>
                </div>
            </form>
        </div>
    );
}

function Section({ title, action, children }) {
    return (
        <section className="bg-white border border-[hsl(var(--border))] rounded-sm p-5">
            <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl">{title}</h2>
                {action}
            </div>
            <div className="space-y-4">{children}</div>
        </section>
    );
}

function MexalSkuLookup({ onPick }) {
    const [q, setQ] = useState("");
    const [items, setItems] = useState([]);
    const [busy, setBusy] = useState(false);

    const search = async () => {
        setBusy(true);
        try {
            const { data } = await api.get("/admin/mexal/unmapped-skus", {
                params: q ? { q, limit: 30 } : { limit: 30 },
            });
            setItems(data.items || []);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Errore ricerca Mexal");
        } finally { setBusy(false); }
    };

    return (
        <div className="mt-4 pt-4 border-t border-[hsl(var(--border))]" data-testid="mexal-sku-lookup">
            <div className="co-caption mb-2">Cerca SKU Mexal non ancora mappato</div>
            <div className="flex gap-2 mb-3">
                <Input
                    placeholder="Ricerca per descrizione (es. Barolo)"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    className="bg-white"
                    data-testid="mexal-sku-search-input"
                />
                <button type="button" onClick={search} disabled={busy} className="co-btn-ghost text-sm" data-testid="mexal-sku-search-btn">
                    <Search className="w-4 h-4" /> {busy ? "Cerca…" : "Cerca"}
                </button>
            </div>
            {items.length > 0 && (
                <div className="max-h-52 overflow-y-auto border border-[hsl(var(--border))] rounded-sm bg-white">
                    {items.map((it) => (
                        <div key={it.codice} className="flex items-center gap-3 p-2 hover:bg-[hsl(var(--secondary))]" data-testid={`mexal-sku-item-${it.codice}`}>
                            <div className="flex-1 min-w-0">
                                <div className="text-sm truncate">{it.descrizione}</div>
                                <div className="text-xs text-[hsl(var(--muted-foreground))]">SKU {it.codice} · {it.um_principale}</div>
                            </div>
                            <button
                                type="button"
                                onClick={() => onPick(it.codice, it.descrizione || "")}
                                className="co-btn-ghost text-xs"
                                data-testid={`mexal-sku-pick-${it.codice}`}
                            >
                                Aggiungi come formato
                            </button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function Field({ label, required, children, action }) {
    return (
        <div>
            <div className="flex items-center justify-between gap-2 mb-1.5">
                <Label className="co-caption block">
                    {label} {required && <span className="text-[hsl(var(--destructive))]">*</span>}
                </Label>
                {action}
            </div>
            {children}
        </div>
    );
}
